import React, {useEffect, useMemo, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTag, faTimes} from "@fortawesome/free-solid-svg-icons";

/**
 * Searchable label filter (typeahead) — scales to many labels where a flat chip
 * list would overflow. Type to filter suggestions, click to add; selected
 * labels show as removable chips.
 *
 * @param {Array<{labelId:number,labelName:string}>} labels - all available labels
 * @param {Array<number>} selectedIds - active label ids
 * @param {(labelId:number)=>void} onToggle - add/remove a label
 * @param {string} [placeholder]
 */
export default function LabelMultiSelect({labels = [], selectedIds = [], onToggle, placeholder = "Type to filter by label…", compact = false}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = useMemo(
    () => labels.filter((l) => selectedIds.includes(l.labelId)),
    [labels, selectedIds]
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return labels
      .filter((l) => !selectedIds.includes(l.labelId))
      .filter((l) => (q ? l.labelName.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [labels, selectedIds, query]);

  const add = (label) => {
    onToggle(label.labelId);
    setQuery("");
    setActiveIndex(0);
    // keep the dropdown open so multiple labels can be picked quickly
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions[activeIndex]) add(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`label-ms${compact ? " label-ms-compact" : ""}`} ref={wrapRef}>
      {/* Selected chips — full mode only. Compact mode shows a count badge in the input. */}
      {!compact && selected.length > 0 && (
        <div className="label-ms-chips">
          {selected.map((l) => (
            <span key={l.labelId} className="label-ms-chip">
              {l.labelName}
              <button type="button" className="label-ms-chip-x" onClick={() => onToggle(l.labelId)} aria-label={`Remove ${l.labelName}`}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="label-ms-input-wrap">
        <FontAwesomeIcon icon={faTag} className="label-ms-icon" />
        <input
          type="text"
          className="label-ms-input"
          placeholder={compact && selected.length > 0
            ? `${selected.length} label${selected.length > 1 ? "s" : ""} selected — type to filter`
            : placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {compact && selected.length > 0 && (
          <span className="label-ms-count" title={selected.map((l) => l.labelName).join(", ")}>
            {selected.length}
          </span>
        )}
      </div>

      {/* Suggestions dropdown */}
      {open && (
        <div className="label-ms-menu">
          {/* In compact mode, show currently-selected labels at the top of the menu
              so the user can deselect them (no chip row above the input). */}
          {compact && selected.length > 0 && (
            <>
              <div className="label-ms-section">Selected</div>
              {selected.map((l) => (
                <button
                  key={`sel-${l.labelId}`}
                  type="button"
                  className="label-ms-option is-selected"
                  onClick={() => onToggle(l.labelId)}
                  title="Click to remove"
                >
                  <FontAwesomeIcon icon={faTag} className="label-ms-option-icon" />
                  {l.labelName}
                  <FontAwesomeIcon icon={faTimes} className="label-ms-option-x" />
                </button>
              ))}
              {suggestions.length > 0 && <div className="label-ms-section">Add</div>}
            </>
          )}
          {suggestions.length === 0 && (!compact || selected.length === 0) ? (
            <div className="label-ms-empty">
              {labels.length === 0 ? "No labels yet" : "No matching labels"}
            </div>
          ) : (
            suggestions.map((l, i) => (
              <button
                key={l.labelId}
                type="button"
                className={`label-ms-option ${i === activeIndex ? "active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => add(l)}
              >
                <FontAwesomeIcon icon={faTag} className="label-ms-option-icon" />
                {l.labelName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
